import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, Grid, makeStyles, Typography } from '@quillbot/design-system';
import MyInputBox from '@Common/quillElements/input/SolidBorder';
import ClearButton from '@Common/quillElements/button/ClearButton';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { RootState } from 'src/_common/models/redux/store';
import {
  handleAuthTrack,
  handleSubscriptionChange,
  login,
  setName,
  signup,
  validateEmail,
} from '../handlers/authentication';
import { loadingAction } from '../actions/authenticationActions';
import Img from '@Common/components/img';

interface IProps {
  onAuthSuccess: Function;
  minimalMode?: boolean;
  handleLoginFlow?: () => void;
}

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: '28px',
    textAlign: 'center',
    paddingTop: '25px',
  },
  subHeader: {
    fontSize: '17px',
    textAlign: 'center',
  },
  signupContainer: {
    margin: 'auto',
    marginTop: '25px',
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: '5px',
    maxWidth: '960px',
    [theme.breakpoints.down('md')]: {
      marginTop: '28px',
    },
  },
  signupContainerInner: {
    padding: '24px 15px 20px',
    maxWidth: '354px',
    margin: 'auto',
    width: '100%',
  },
  socialBtn: {
    // backgroundColor: '#f1f2fa',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    padding: '12px',
    width: '49%',
    height: '45px',
    position: 'relative',
    '&:hover': {
      // backgroundColor: '#f6f6f9',
    },
    '& img': {
      position: 'absolute',
      left: '15px',
      height: '16px',
      [theme.breakpoints.down(360)]: {
        left: '12px',
      },
    },

    [theme.breakpoints.down(360)]: {
      width: '100%',
      marginBottom: 5,
      minWidth: '140px',
    },
  },
  socialBtnText: {
    fontSize: '12px',
  },
  iconBtn: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: '3px',
    position: 'absolute',
    top: '14px',
    right: 7.5,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },

  inputContainer: {
    position: 'relative',
  },
  helperText: {
    margin: 'auto',
  },
  checkBoxLabel: {
    margin: 0,
  },
  checkBox: {
    padding: '4px 4px 4px 0px',
  },
  continueBtn: {
    padding: '10px 16px 11px;',
  },
}));

type SignUpQuery = 'google' | 'facebook' | '';
function Signup(props: IProps) {
  const classes = useStyles(props);
  const [passwordHelperText, setPasswordHelperText] = useState('');
  const emailField = useRef() as MutableRefObject<HTMLInputElement>;
  const passwordField = useRef() as MutableRefObject<HTMLInputElement>;
  const nameField = useRef() as MutableRefObject<HTMLInputElement>;
  const extensionMetaData = useSelector((state: RootState) => state.common.platform.extensionMetaData);
  const isWordExt = extensionMetaData?.type === 'ms-word';
  const dispatch = useDispatch();
  const router = useRouter();
  const initErrorObj: {
    errorMessage: string;
    emailValidationError: string;
    errorFields: string[];
  } = {
    errorMessage: '',
    emailValidationError: '',
    errorFields: [],
  };
  const [error, setError] = useState(initErrorObj);

  const handleSignup = () => {
    const username = emailField?.current.value;
    const password = passwordField.current ? passwordField?.current.value : '';
    const name = nameField.current ? nameField?.current.value : '';
    if (!validateEmail(username)) {
      setError({
        emailValidationError: 'Please enter valid email',
        errorMessage: '',
        errorFields: [],
      });
      return;
    }
    if (!password || password.length < 6) {
      setError({
        errorMessage: '',
        errorFields: ['password'],
        emailValidationError: '',
      });
      setPasswordHelperText('Minimum 6 characters');
      return;
    }
    if (/^\s{1,}$/.test(password)) {
      setError({
        errorMessage: '',
        errorFields: ['password'],
        emailValidationError: '',
      });
      setPasswordHelperText('Only Spaces not allowed');
      return;
    }
    if (password.length > 72) {
      setError({
        errorMessage: '',
        errorFields: ['password'],
        emailValidationError: '',
      });
      setPasswordHelperText('Maximum 72 characters');
      return;
    }
    if (!name) {
      setError({
        errorMessage: '',
        errorFields: ['name'],
        emailValidationError: '',
      });
      return;
    }
    dispatch(loadingAction(true));
    signup(username, password)
      .then((res) => {
        props.onAuthSuccess(res);
      })
      .catch((e) => {
        dispatch(loadingAction(false));
        if (!e) {
          return;
        }
        setError(e);
      });
  };

  const handleKeyup = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // hanle enter click
    if (event.keyCode === 13) {
      event.preventDefault();
      handleSignup();
    }
  };
  const handleLogin = (method: 'google' | 'facebook' | 'email') => {
    login(method)
      .then((res) => props.onAuthSuccess(res))
      .catch((e) => {
        setError({ ...e });
      });
  };

  useEffect(() => {
    handleAuthTrack(router);
    const queryObj = router.query;
    const signUpBy: SignUpQuery = queryObj?.signup_by as SignUpQuery;
    if (signUpBy) {
      handleLogin(signUpBy);
      delete queryObj.signup_by;
      router.replace({
        pathname: router.pathname,
        query: { ...queryObj },
      });
    }
  }, []);

  const getSubmitBtn = () => (
    <Button
      fullWidth
      variant="contained"
      color="primary"
      className="auth-btn"
      classes={{ root: classes.continueBtn }}
      onClick={handleSignup}>
      Continue
    </Button>
  );

  return (
    <Grid container alignItems="center">
      {!props.minimalMode ? (
        <>
          <Grid item xs={12}>
            <Typography component="h1" classes={{ body1: classes.header }}>
              Sign up for your free QuillBot account
            </Typography>
          </Grid>
          {/* <Grid item xs={12}>
            <Typography classes={{ body1: classes.subHeader }}>
              Paraphrase {CHAR_LIMITS.PARAPHRASER_WORD_LIMIT_MEMBER} characters with a free account.
            </Typography>
          </Grid> */}
        </>
      ) : (
        <></>
      )}

      <Grid item xs={12} className={!props.minimalMode ? classes.signupContainer : ''}>
        {/* <Grid container className={!props.minimalMode ? 'auth-container-top-border' : ''} /> */}
        <Grid container direction="column" alignItems="center" className={classes.signupContainerInner} spacing={1}>
          {!isWordExt && (
            <>
              <Grid item container justifyContent="space-between">
                <Button
                  classes={{
                    root: classes.socialBtn,
                    text: classes.socialBtnText,
                  }}
                  onClick={() => {
                    handleLogin('google');
                  }}>
                  <Img src="/emojis/solid_google.svg" alt="google logo" />
                  With Google
                </Button>
                <Button
                  onClick={() => handleLogin('facebook')}
                  classes={{
                    root: classes.socialBtn,
                    text: classes.socialBtnText,
                  }}>
                  <Img src="/emojis/solid_facebook.svg" alt="facebook logo" />
                  With Facebook
                </Button>
              </Grid>
              <Grid item container>
                <Typography align="center" variant="caption" style={{ textAlign: 'center', width: '100%' }}>
                  OR
                </Typography>
              </Grid>
            </>
          )}
          <Grid item container className={classes.inputContainer} alignItems="center">
            <MyInputBox
              fullWidth
              required
              sensitive
              id="username"
              size="small"
              label="Email"
              onKeyUp={handleKeyup}
              variant="filled"
              inputRef={emailField}
              error={Boolean(error.emailValidationError || (error.errorMessage && error.errorFields.includes('email')))}
              helperText={error.emailValidationError}
              onChange={() => {
                setError(initErrorObj);
              }}
            />
          </Grid>

          <Grid item container className={classes.inputContainer} alignItems="center">
            <MyInputBox
              fullWidth
              required
              sensitive
              size="small"
              id="password"
              type="password"
              label="Password"
              onKeyUp={handleKeyup}
              onFocus={() => !error.errorFields.includes('password') && setPasswordHelperText('Minimum 6 characters')}
              onBlur={() => !error.errorFields.includes('password') && setPasswordHelperText('')}
              inputRef={passwordField}
              /* FormHelperTextProps={{
                classes: { contained: classes.helperText },
              }} */
              variant="filled"
              error={error.errorFields.includes('password')}
              helperText={passwordHelperText}
              onChange={() => {
                if (error.errorFields.includes('password')) {
                  setError(initErrorObj);
                }
              }}
            />
          </Grid>

          <Grid item container className={classes.inputContainer} alignItems="center">
            <MyInputBox
              fullWidth
              required
              sensitive
              size="small"
              id="name"
              type="text"
              onKeyUp={handleKeyup}
              inputRef={nameField}
              label="Name"
              variant="filled"
              helperText={error.errorFields.includes('name') && 'Required'}
              error={error.errorFields.includes('name')}
              onChange={setName}
            />
          </Grid>

          <Grid item container>
            <FormGroup aria-label="position" row>
              <FormControlLabel
                classes={{ root: classes.checkBoxLabel }}
                value="start"
                control={
                  <Checkbox
                    classes={{ root: classes.checkBox }}
                    size="small"
                    onChange={handleSubscriptionChange}
                    name="marketingCheckbox"
                    color="primary"
                  />
                }
                label={<Typography variant="caption">Send me news, promotions, and product emails</Typography>}
                labelPlacement="end"
              />
            </FormGroup>
          </Grid>
          {error.errorMessage && (
            <Grid item container>
              <Typography variant="caption" color="error">
                {/* handle auth popup blocked error in word ext */}
                {error.errorMessage === 'wordPopupError' ? (
                  <>
                    Logging in with Google or Facebook is currently bugged - to temporarily fix this, please visit{' '}
                    <a
                      className="btn-link"
                      href="https://help.quillbot.com/hc/en-us/articles/360056805014-I-can-t-log-in-with-Google-or-Facebook-What-should-I-do-"
                      target="_blank"
                      rel="noreferrer">
                      here
                    </a>{' '}
                    and follow the instructions
                  </>
                ) : (
                  error.errorMessage
                )}
              </Typography>
            </Grid>
          )}
          <Grid item container>
            {getSubmitBtn()}
          </Grid>

          <Grid item xs={12} style={{ paddingTop: '8px', paddingBottom: '8px' }} className="qbp-textCenter">
            <Typography variant="body2">
              {props.minimalMode ? (
                <ClearButton className="btn-link" type="button" onClick={props.handleLoginFlow}>
                  Already have an account?
                </ClearButton>
              ) : (
                <Link href={{ pathname: '/login', query: { ...router.query } }}>
                  <ClearButton className="btn-link" type="button">
                    Already have an account?
                  </ClearButton>
                </Link>
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          item
          container
          justifyContent="center"
          style={{
            alignItems: 'center',
            marginTop: '4px',
            maxWidth: '400px',
            margin: '0 auto 24px',
            opacity: 0.6,
          }}>
          <Typography
            style={{
              fontSize: '10px',
              textAlign: 'center',
            }}
            variant="caption">
            By signing in, you agree with QuillBot‘s{' '}
            <a
              href="terms"
              target="_blank"
              className="btn-link"
              style={{ color: 'inherit', textDecoration: 'underline' }}>
              Terms of Service{' '}
            </a>
            and acknowledge that your personal information will be processed in accordance with QuillBot’s{' '}
            <a
              href="privacy"
              target="_blank"
              className="btn-link"
              style={{ color: 'inherit', textDecoration: 'underline' }}>
              Privacy Policy
            </a>
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default Signup;
